/**
 * useConfiguredRequestProviders — the Media Requests provider families
 * (`seerr` / `ombi`) that are present box-wide beyond the viewer's own package
 * set: a stored key override (a remote-configured request manager) OR a
 * box-wide install under any user. Read from the admin/grant-gated settings GET
 * (`/api/media-requests/settings`), whose per-provider `configured` and
 * `installed` flags report both without ever carrying the key.
 *
 * Fetched ONLY when the caller holds the requests grant (the endpoint 403s for
 * everyone else, so a nav render must never fire it ungated), once per session
 * behind a module cache with the same 5-minute TTL discipline `useSystemApps`
 * uses. A failed or absent fetch resolves to an empty set, so the local-install
 * nav path (`systemInstalledApps`) still governs on its own.
 */
import { useEffect, useState } from "react";

const CACHE_TTL_MS = 5 * 60 * 1000;

let cached: Set<string> | null = null;
let cachedAt = 0;
let inFlight: Promise<Set<string>> | null = null;

const isFresh = (): boolean => cached !== null && Date.now() - cachedAt < CACHE_TTL_MS;

const fetchConfigured = async (): Promise<Set<string>> => {
  try {
    const res = await fetch("/api/media-requests/settings", {
      credentials: "include",
      headers: { Accept: "application/json" },
    });
    const data = await res.json();
    if (!res.ok || !data?.ok || !data.data?.providers) return new Set();
    const providers = data.data.providers as Record<string, { configured?: boolean; installed?: boolean }>;
    const present = new Set<string>();
    for (const [id, provider] of Object.entries(providers)) {
      // Box-wide presence: a stored key override (remote) OR an install under
      // any user. Either means "a request manager exists on this server".
      if (provider?.configured || provider?.installed) present.add(id);
    }
    return present;
  } catch {
    return new Set();
  }
};

export const useConfiguredRequestProviders = (enabled: boolean): ReadonlySet<string> => {
  const [providers, setProviders] = useState<Set<string>>(() => (isFresh() ? cached! : new Set()));

  useEffect(() => {
    if (!enabled) return;
    if (isFresh()) {
      setProviders(cached!);
      return;
    }
    let active = true;
    inFlight ??= fetchConfigured().then((result) => {
      cached = result;
      cachedAt = Date.now();
      inFlight = null;
      return result;
    });
    void inFlight.then((result) => {
      if (active) setProviders(result);
    });
    return () => {
      active = false;
    };
  }, [enabled]);

  return providers;
};

/** Test/HMR seam — drop the session cache so the next enabled mount refetches. */
export const invalidateConfiguredRequestProvidersCache = (): void => {
  cached = null;
  cachedAt = 0;
  inFlight = null;
};
